import { useForm } from "react-hook-form";
import { baseURL } from '../config/config';
import { post } from "../utils/apiManager";
import { toast } from 'react-toastify';
import { Link, useNavigate } from 'react-router-dom';
import { LoginDetails } from "../reduxToolkit/slice/auth";
import { useDispatch } from "react-redux";
import { useState } from "react";

const Login = () => {
  const { register, handleSubmit, formState: { errors } } = useForm();
  const Navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  // Two-step sign-in: after the password, a code sent by email and/or text
  const [twoStep, setTwoStep] = useState(null); // { tempToken, sentTo }
  const [code, setCode] = useState("");

  const finishLogin = (data) => {
    toast.success(data?.message);
    dispatch(LoginDetails({
      token: data?.token,
      name: data?.data?.name,
      userId: data?.data?._id,
      email: data?.data?.email,
      isLogin: true,
      profileImage: data?.data?.profileImage,
    }));
    Navigate("/admin/index");
  };

  const submitCode = (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(code.trim())) { toast.error("Enter the 6-digit code"); return; }
    setLoading(true);
    post(`${baseURL}/admin/verify-login-2fa`, { tempToken: twoStep.tempToken, code: code.trim() })
      .then((data) => {
        setLoading(false);
        if (data?.status && data?.token) finishLogin(data);
        else toast.error(data?.message || "That code is not right.");
      })
      .catch(() => { setLoading(false); toast.error("Could not check the code"); });
  };

  const resendCode = () => {
    post(`${baseURL}/admin/resend-login-2fa`, { tempToken: twoStep.tempToken })
      .then((data) => (data?.status ? toast.success(data.message) : toast.error(data?.message)));
  };

  const onSubmit = (data) => {
    setLoading(true);
    const Url = `${baseURL}/admin/login`;
    post(Url, data)
      .then((data) => {
        setLoading(false);
        if (data?.status && data?.requires2FA) {
          setTwoStep({ tempToken: data.tempToken, sentTo: data.sentTo || [] });
          setCode("");
          toast.info(data.message);
          return;
        }
        if (data?.status) {
          toast.success(data?.message);
          dispatch(LoginDetails({
            token: data?.token,
            name: data?.data?.name,
            userId: data?.data?._id,
            email: data?.data?.email,
            isLogin: true,
            profileImage: data?.data?.profileImage,
          }));
          Navigate("/admin/index");
        } else {
          toast.error(data?.message);
        }
      })
      .catch((err) => {
        setLoading(false);
        toast.error(err?.response?.data?.error || "Login failed");
      });
  };

  const inputStyle = (hasError) => ({
    width: "100%",
    padding: "11px 14px",
    borderRadius: 10,
    border: `1.5px solid ${hasError ? "#AD2429" : "#e2e8f0"}`,
    fontSize: 15,
    color: "#0a1628",
    background: "#fff",
    outline: "none",
    boxSizing: "border-box",
    transition: "border-color .15s",
    fontFamily: "inherit",
  });

  const labelStyle = {
    fontSize: 13,
    fontWeight: 600,
    color: "#3a4a6a",
    marginBottom: 6,
    display: "block",
  };

  return (
    <div style={{ fontFamily: "'Segoe UI', system-ui, sans-serif" }}>

      {/* Heading */}
      <div style={{ marginBottom: 28 }}>
        <h2 style={{
          fontSize: 22,
          fontWeight: 700,
          color: "#0a1628",
          margin: "0 0 6px",
          letterSpacing: "-0.5px",
        }}>
          Welcome back
        </h2>
        <p style={{ fontSize: 14, color: "#8a9ab8", margin: 0 }}>
          Sign in to your admin account
        </p>
      </div>

      {twoStep && (
        <form onSubmit={submitCode}>
          <p style={{ fontSize: 14, color: "#3a4a6a", margin: "0 0 14px" }}>
            Enter the 6-digit code we sent to {twoStep.sentTo.join(" and ") || "you"}.
          </p>
          <input
            autoFocus
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            placeholder="123456"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            style={{ ...inputStyle(false), fontSize: 22, letterSpacing: 8, textAlign: "center", marginBottom: 16 }}
          />
          <button type="submit" disabled={loading}
            style={{ width: "100%", padding: "13px", borderRadius: 10, border: "none", background: loading ? "#c0614f" : "#AD2429", color: "#fff", fontSize: 16, fontWeight: 700, cursor: loading ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
            {loading ? "Checking..." : "Verify and sign in"}
          </button>
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 14, fontSize: 13 }}>
            <button type="button" onClick={resendCode} style={{ background: "none", border: "none", color: "#AD2429", cursor: "pointer", padding: 0, fontFamily: "inherit" }}>Send a new code</button>
            <button type="button" onClick={() => setTwoStep(null)} style={{ background: "none", border: "none", color: "#64748b", cursor: "pointer", padding: 0, fontFamily: "inherit" }}>Back</button>
          </div>
        </form>
      )}

      <form onSubmit={handleSubmit(onSubmit)} style={{ display: twoStep ? "none" : "block" }}>

        {/* Email */}
        <div style={{ marginBottom: 18 }}>
          <label style={labelStyle}>Email address</label>
          <input
            placeholder="you@bidbuyzoom.com"
            type="email"
            style={inputStyle(errors.email)}
            autoComplete="email"
            {...register("email", { required: true })}
          />
          {errors.email && (
            <span style={{ fontSize: 12, color: "#AD2429", marginTop: 4, display: "block" }}>
              Email is required
            </span>
          )}
        </div>

        {/* Password */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <label style={{ ...labelStyle, marginBottom: 0 }}>Password</label>
            <Link
              to="/auth/forgot-password"
              style={{ fontSize: 13, color: "#AD2429", textDecoration: "none", fontWeight: 500 }}
            >
              Forgot password?
            </Link>
          </div>
          <div style={{ position: "relative" }}>
            <input
              placeholder="••••••••"
              type={showPassword ? "text" : "password"}
              style={{ ...inputStyle(errors.password), paddingRight: 42 }}
              {...register("password", { required: true })}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              style={{
                position: "absolute",
                right: 12,
                top: "50%",
                transform: "translateY(-50%)",
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "#9aa5c0",
                fontSize: 16,
                padding: 0,
                lineHeight: 1,
              }}
            >
              {showPassword ? "🙈" : "👁️"}
            </button>
          </div>
          {errors.password && (
            <span style={{ fontSize: 12, color: "#AD2429", marginTop: 4, display: "block" }}>
              Password is required
            </span>
          )}
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={loading}
          style={{
            width: "100%",
            padding: "13px",
            borderRadius: 10,
            border: "none",
            background: loading ? "#c0614f" : "#AD2429",
            color: "#fff",
            fontSize: 16,
            fontWeight: 700,
            cursor: loading ? "not-allowed" : "pointer",
            boxShadow: "0 4px 16px rgba(173,36,41,.25)",
            letterSpacing: "-0.2px",
            transition: "background .15s",
            fontFamily: "inherit",
          }}
        >
          {loading ? "Signing in..." : "Sign In"}
        </button>

      </form>
    </div>
  );
};

export default Login;
